import React, { useCallback, useEffect, useRef, useState } from "react";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import Check from "@enerex/unified-ui-react/form/Check";
import FormInput from "@enerex/unified-ui-react/form/FormInput";
import FormSelect from "@enerex/unified-ui-react/form/Select";
import Select from "components/UnifiedSelect";
import { useForm } from "react-hook-form";
import * as api from "../utilityApi";
import { CommodityIcon } from "../CommodityIcons";
import { getIdentifier, getStateDisplayName, getStateName, getStateOptionValue, toOption, utilitySelectStyles } from "../utilityUtils";
import "./EditStateOperationModal.css";
const StateOperationModal = ({
  show,
  onHide,
  onSave,
  countries = [],
  states = [],
  existingStates = [],
  onCountryChange,
  saving,
}) => {
  const [form, setForm] = useState({
    CountryIdentifier: "",
    StateId: "",
    CommodityIds: [1],
  });
  const [modalCountries, setModalCountries] = useState([]);
  const [modalStates, setModalStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const statesRef = useRef(states);
  const { control, getValues, reset } = useForm({
    defaultValues: {
      ServiceReferenceNumberRequired: false,
      ServiceReferenceNumberDescription: "",
      KeyNameRequired: false,
      KeyNameDescription: "",
      EnrollmentLeadTime: "",
      CommodityElectric: true,
      CommodityGas: false,
    },
  });

  useEffect(() => {
    statesRef.current = states;
  }, [states]);

  const loadStatesForCountry = useCallback(async (countryIdentifier) => {
    setLoadingStates(true);
    try {
      const nextStates = onCountryChange
        ? await onCountryChange(countryIdentifier)
        : countryIdentifier
          ? await api.getStatesByCountry(countryIdentifier)
          : await api.getStates();

      const nextModalStates = Array.isArray(nextStates) && nextStates.length ? nextStates : statesRef.current;
      setModalStates(Array.isArray(nextModalStates) ? nextModalStates : []);
    } catch (error) {
      console.error("Failed to load states for country", error);
      setModalStates([]);
    } finally {
      setLoadingStates(false);
    }
  }, [onCountryChange]);

  useEffect(() => {
    if (!show || countries.length) return;

    let active = true;
    api.getCountries()
      .then((nextCountries) => { if (active) setModalCountries(Array.isArray(nextCountries) ? nextCountries : []); })
      .catch((error) => {
        console.error("Failed to load countries", error);
        if (active) setModalCountries([]);
      });

    return () => { active = false; };
  }, [countries.length, show]);

  const availableCountries = countries.length ? countries : modalCountries;

  useEffect(() => {
    if (show) {
      const defaultCountry =
        availableCountries.find((country) => (country.name || country.Name) === "USA") ||
        availableCountries[0];
      const defaultCountryIdentifier = getIdentifier(defaultCountry) || "";
      setForm({
        CountryIdentifier: defaultCountryIdentifier,
        StateId: "",
        StateName: "",
        CommodityIds: [1],
      });
      reset({
        ServiceReferenceNumberRequired: false,
        ServiceReferenceNumberDescription: "",
        KeyNameRequired: false,
        KeyNameDescription: "",
        EnrollmentLeadTime: "",
        CommodityElectric: true,
        CommodityGas: false,
      });
      loadStatesForCountry(defaultCountryIdentifier);
    }
  }, [availableCountries, existingStates, loadStatesForCountry, reset, show]);

  const countryOptions = availableCountries.map((c) => toOption(c));
  const stateOptions = modalStates.map((s) => ({
    value: getStateOptionValue(s),
    label: getStateDisplayName(s) || getStateName(s),
    raw: s,
  }));
  const selectedCountry =
    countryOptions.find((option) => String(option.value) === String(form.CountryIdentifier)) || null;

  const handleCountryChange = async (value) => {
    const countryIdentifier = value?.value || "";
    setForm((f) => ({ ...f, CountryIdentifier: countryIdentifier, StateId: "", StateName: "" }));
    await loadStatesForCountry(countryIdentifier);
  };

  return (
    <ConfirmationModal
      dialogClassName="utility-sop-edit-dialog"
      centered
      show={show}
      onHide={onHide}
      title="Add State of Operations"
      confirmButtonLabel={saving ? "Adding..." : "Add State"}
      confirmButtonVariant="primary"
      isLoading={saving}
      onConfirm={() => {
        const values = getValues();
        const commodityIds = [values.CommodityElectric && 1, values.CommodityGas && 2].filter(Boolean);
        if (!form.StateId || !commodityIds.length) return;
        const selectedRawState = modalStates.find((s) => String(getStateOptionValue(s)) === String(form.StateId));
        onSave({
          ...form,
          CommodityIds: commodityIds,
          SelectedState: selectedRawState || null,
          ServiceReferenceNumberRequired: Boolean(values.ServiceReferenceNumberRequired),
          ServiceReferenceNumberDescription: values.ServiceReferenceNumberDescription || null,
          KeyNameRequired: Boolean(values.KeyNameRequired),
          KeyNameDescription: values.KeyNameDescription ?? "",
          EnrollmentLeadTime: values.EnrollmentLeadTime ?? "",
        });
      }}
    >
      <div className="utility-sop-edit-fields">
        <label htmlFor="utility-state-country">Country <span className="utility-sop-required">*</span></label>
        <Select
          inputId="utility-state-country"
          options={countryOptions}
          value={selectedCountry}
          onChange={handleCountryChange}
          placeholder="Select country"
          styles={utilitySelectStyles}
          isDisabled={saving}
          noOptionsMessage={() => "No countries found"}
          menuPortalTarget={document.body}
        />
        <label htmlFor="utility-state-state">State <span className="utility-sop-required">*</span></label>
        <FormSelect
          inputId="utility-state-state"
          options={stateOptions}
          value={stateOptions.find((o) => String(o.value) === String(form.StateId)) || null}
          onChange={(value) => setForm((f) => ({ ...f, StateId: value?.value || "", StateName: value?.label || "" }))}
          placeholder="Select state"
          isDisabled={saving}
          isLoading={loadingStates}
          noOptionsMessage={() => loadingStates ? "Loading states..." : "No states found"}
          isClearable
          getOptionValue={(option) => String(option.value)}
          getOptionLabel={(option) => option.label}
          menuPortalTarget={document.body}
        />
        <fieldset className="utility-sop-edit-commodities" aria-label="Commodities" disabled={saving}>
          <Check name="CommodityElectric" control={control} label={<CommodityIcon type="electric" />} disabled={saving} />
          <Check name="CommodityGas" control={control} label={<CommodityIcon type="gas" />} disabled={saving} />
        </fieldset>
        <Check
          id="utility-add-name-key-required"
          name="KeyNameRequired"
          control={control}
          label="Name Key Required"
          disabled={saving}
        />
        <FormInput type="text" name="KeyNameDescription" control={control} label="Name Key Description" disabled={saving} />
        <Check
          id="utility-add-service-reference-required"
          name="ServiceReferenceNumberRequired"
          control={control}
          label="Service Reference Number Required"
          disabled={saving}
        />
        <FormInput type="text" name="ServiceReferenceNumberDescription" control={control} label="Service Reference Number Description" disabled={saving} />
        <FormInput
          type="number"
          name="EnrollmentLeadTime"
          control={control}
          label="Enrollment Lead Time days"
          min={0}
          step={1}
          disabled={saving}
        />
      </div>
    </ConfirmationModal>
  );
};

export default StateOperationModal;

